/**
 * Shared bits for the Masters compliance tabs.
 * @file src/web/features/admin/components/masters/shared.tsx
 *
 * The compliance tabs (Registrations, Banking, Series, Items, Gaps) all have
 * the same shape: load a list for the selected entity, add a row through a
 * small form, edit or delete a row, refetch. This holds the pieces that would
 * otherwise be copied five times, and matches the visual language already used
 * by the Company/Parties/Groups/Ledgers tabs.
 *
 * `useEntityResource` deliberately refetches on entity change and exposes a
 * `reload` so a mutation can pull fresh server truth rather than patching local
 * state — these screens feed a gap report, and a stale count there is worse
 * than a re-fetch.
 */

import { useCallback, useEffect, useState } from 'react';
import { Loader2, AlertTriangle, Plus, X, ChevronDown, Building2, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useCompany } from '../../lib/company-context';
import { adminFetch, adminFetchJson } from '../../lib/admin-api';

/* ── data ───────────────────────────────────────────────────────────────── */

export function useEntityResource<T>(path: string, entityId: string | null, deps: unknown[] = []) {
  const [items, setItems] = useState<T[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(() => {
    if (!entityId) { setError('Select an entity in the header.'); setLoading(false); return; }
    setLoading(true);
    adminFetchJson<{ items: T[] }>(`/api/masters/${path}`, { query: { entityId } })
      .then((d) => { setItems(d.items ?? []); setError(null); })
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, entityId, ...deps]);

  useEffect(reload, [reload]);
  return { items, loading, error, reload, setError };
}

/** POST/PATCH/DELETE against a masters route; throws the server's message. */
export async function mutate(
  path: string, method: 'POST' | 'PATCH' | 'DELETE', body: Record<string, unknown>,
): Promise<void> {
  const res = await adminFetch(`/api/masters/${path}`, {
    method,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const d = await res.json().catch(() => ({}));
    throw new Error(d.message || `Request failed (${res.status})`);
  }
}

/* ── layout ─────────────────────────────────────────────────────────────── */

/**
 * A titled panel. Pass `collapsible` to turn the header into a bar that folds
 * the body away — the Entity tab stacks six of these and is otherwise a very
 * long scroll.
 *
 * `summary` is what the bar says while it is shut, so a closed section still
 * answers "is there anything in here?" without being opened. A count alone is
 * enough; a missing-something warning is better.
 *
 * The body is hidden with CSS rather than unmounted, so a half-typed add form
 * survives an accidental collapse and the section does not refetch on reopen.
 */
export function Card({ title, subtitle, action, summary, collapsible, defaultOpen = true, children }: {
  title: string; subtitle?: string; action?: React.ReactNode;
  summary?: React.ReactNode; collapsible?: boolean; defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const isOpen = !collapsible || open;

  const heading = (
    <div className="min-w-0 text-left">
      <h3 className="text-base font-semibold text-ink-900">
        {title}
        {summary && <span className="ml-2 text-sm font-normal text-ink-500">{summary}</span>}
      </h3>
      {subtitle && isOpen && <p className="mt-1 max-w-2xl text-sm text-ink-500">{subtitle}</p>}
    </div>
  );

  // Collapsed bars stack, so they sit tighter than standalone cards. Every
  // existing non-collapsible Card keeps its original spacing.
  return (
    <div className={`${collapsible ? 'mb-3' : 'mb-6'} overflow-hidden rounded-2xl border border-ink-200 bg-white`}>
      <div className={`flex flex-wrap items-start justify-between gap-3 px-6 py-5 ${isOpen ? 'border-b border-ink-100' : ''}`}>
        {collapsible ? (
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            aria-expanded={isOpen}
            className="flex min-w-0 flex-1 items-start gap-2.5 rounded-lg text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-highlighter"
          >
            <ChevronDown
              className={`mt-1 h-4 w-4 shrink-0 text-ink-400 transition-transform ${isOpen ? '' : '-rotate-90'}`}
              aria-hidden
            />
            {heading}
          </button>
        ) : heading}
        {/* Hidden while shut: an "Add GSTIN" button above a folded section reads
            as if it would add to something else. */}
        {isOpen && action}
      </div>
      <div className={isOpen ? undefined : 'hidden'}>{children}</div>
    </div>
  );
}

export function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <label className="block text-xs font-medium text-ink-600">{label}</label>
      {children}
      {hint && <p className="text-[11px] text-ink-400">{hint}</p>}
    </div>
  );
}

export const inputCls =
  'w-full rounded-xl border border-ink-200 bg-white px-3 py-2 text-sm focus:border-ink-300 focus-visible:outline-none';

export function TextInput({ value, onChange, placeholder, mono, type = 'text' }: {
  value: string; onChange: (v: string) => void; placeholder?: string; mono?: boolean; type?: string;
}) {
  return (
    <input
      type={type} value={value} placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      className={`${inputCls} ${mono ? 'font-mono' : ''}`}
    />
  );
}

export function Select({ value, onChange, options, placeholder }: {
  value: string; onChange: (v: string) => void; options: readonly (string | { value: string; label: string })[]; placeholder?: string;
}) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} className={inputCls}>
      <option value="">{placeholder ?? '—'}</option>
      {options.map((o) => {
        const v = typeof o === 'string' ? o : o.value;
        const l = typeof o === 'string' ? o : o.label;
        return <option key={v} value={v}>{l}</option>;
      })}
    </select>
  );
}

export function PrimaryBtn({ onClick, busy, children, type = 'button' }: {
  onClick?: () => void; busy?: boolean; children: React.ReactNode; type?: 'button' | 'submit';
}) {
  return (
    <button
      type={type} onClick={onClick} disabled={busy}
      className="inline-flex items-center gap-2 rounded-full bg-inkblack px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-inkblack-80 disabled:opacity-60"
    >
      {busy && <Loader2 className="h-4 w-4 animate-spin" />}
      {children}
    </button>
  );
}

export function GhostBtn({ onClick, children, tone = 'neutral' }: {
  onClick: () => void; children: React.ReactNode; tone?: 'neutral' | 'danger';
}) {
  const cls = tone === 'danger'
    ? 'border-danger-200 text-danger-600 hover:bg-danger-50'
    : 'border-ink-300 text-ink-600 hover:bg-ink-50';
  return (
    <button onClick={onClick} className={`inline-flex items-center gap-1 rounded-full border bg-white px-2.5 py-1 text-[11px] font-semibold transition ${cls}`}>
      {children}
    </button>
  );
}

export function AddToggle({ open, onToggle, label }: { open: boolean; onToggle: () => void; label: string }) {
  return (
    <button
      onClick={onToggle}
      className="inline-flex items-center gap-1.5 rounded-full border border-ink-300 bg-white px-3.5 py-1.5 text-xs font-semibold text-ink-700 transition hover:bg-ink-50"
    >
      {open ? <X className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />}
      {open ? 'Cancel' : label}
    </button>
  );
}

/**
 * Type-to-confirm delete. Every delete in these tabs is a hard DELETE with no
 * undo — a mis-clicked row takes a GSTIN, a PF code or an employee's whole
 * salary history with it, and the gap report goes quiet about data that was
 * never actually collected. A native confirm() is one keypress from being
 * dismissed by muscle memory, so this asks for the word.
 *
 * const { askDelete, deleteDialog } = useDeleteConfirm();
 * <GhostBtn onClick={() => askDelete('GSTIN 29ABCDE1234F1Z5', () => remove(id))} />
 * {deleteDialog}
 */
export function useDeleteConfirm() {
  const [pending, setPending] = useState<{ what: string; run: () => Promise<void> | void } | null>(null);
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);

  const askDelete = useCallback((what: string, run: () => Promise<void> | void) => {
    setTyped('');
    setPending({ what, run });
  }, []);

  const close = useCallback(() => { setPending(null); setTyped(''); setBusy(false); }, []);

  const armed = typed.trim().toLowerCase() === 'yes';

  const deleteDialog = pending ? (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink-900/40 p-4"
      role="dialog"
      aria-modal="true"
      onClick={close}
    >
      <div className="w-full max-w-md rounded-2xl border border-ink-200 bg-white p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start gap-3">
          <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-danger-50">
            <AlertTriangle className="h-4 w-4 text-danger-600" aria-hidden />
          </div>
          <div className="min-w-0">
            <h3 className="text-base font-semibold text-ink-900">Delete {pending.what}?</h3>
            <p className="mt-1 text-sm text-ink-600">
              <span className="font-semibold text-danger-700">This deletion is permanent</span> and cannot be undone.
            </p>
          </div>
        </div>

        <label className="mt-5 block text-xs font-medium text-ink-600">
          Type <span className="font-mono font-semibold text-ink-800">yes</span> to confirm
        </label>
        <input
          autoFocus
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Escape') close(); }}
          placeholder="yes"
          className={`${inputCls} mt-1.5`}
        />

        <div className="mt-5 flex items-center justify-end gap-3">
          <button onClick={close} className="text-sm font-medium text-ink-500 hover:text-ink-700">Cancel</button>
          <button
            disabled={!armed || busy}
            onClick={async () => {
              setBusy(true);
              try { await pending.run(); close(); }
              finally { setBusy(false); }
            }}
            className="inline-flex items-center gap-2 rounded-full bg-danger-600 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-danger-500 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            Delete permanently
          </button>
        </div>
      </div>
    </div>
  ) : null;

  return { askDelete, deleteDialog };
}

export function FormError({ msg }: { msg: string | null }) {
  if (!msg) return null;
  return (
    <div className="mb-4 flex items-start gap-2 rounded-xl border border-danger-200 bg-danger-50 px-4 py-3 text-sm text-danger-700">
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
      <span>{msg}</span>
    </div>
  );
}

/**
 * Shown instead of a tab when no client is selected.
 *
 * This used to go through the error path, so an ordinary first-run state
 * rendered as a red "Failed to load: Select an entity in the header." Nothing
 * had failed, and pointing at another control is worse than offering the
 * choice here — so the list is right in the panel.
 */
export function PickEntity() {
  const { entities, setEntityId, loading } = useCompany();

  if (loading) return <Loading label="Loading your clients" />;

  if (!entities.length) {
    return (
      <div className="rounded-2xl border border-dashed border-ink-300 bg-ink-50/50 px-6 py-14 text-center">
        <Building2 className="mx-auto h-8 w-8 text-ink-400" />
        <h2 className="mt-3 text-base font-semibold text-ink-900">No clients yet</h2>
        <p className="mx-auto mt-1 max-w-md text-sm text-ink-600">
          Masters belong to a client. Add one, then come back to record its PAN,
          GST registrations, parties and chart of accounts.
        </p>
        <Link to="/admin/entities" className="mt-5 inline-flex items-center gap-1.5 rounded-full bg-inkblack px-5 py-2.5 text-sm font-semibold text-paper no-underline">
          Add a client <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-ink-200 bg-white px-6 py-12 text-center">
      <Building2 className="mx-auto h-8 w-8 text-ink-400" />
      <h2 className="mt-3 text-base font-semibold text-ink-900">Pick a client to continue</h2>
      <p className="mx-auto mt-1 max-w-md text-sm text-ink-600">
        Masters are recorded per client — PAN, GST registrations, parties and the
        chart of accounts all belong to one set of books.
      </p>

      {/* A handful reads faster as buttons; a long list does not. */}
      {entities.length <= 8 ? (
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          {entities.map((e) => (
            <button
              key={e.id}
              onClick={() => setEntityId(e.id)}
              className="inline-flex items-center gap-1.5 rounded-full border border-ink-300 bg-white px-4 py-2 text-sm font-medium text-ink-800 transition hover:border-inkblack hover:bg-ink-50"
            >
              {e.name}
            </button>
          ))}
        </div>
      ) : (
        <select
          onChange={(ev) => ev.target.value && setEntityId(ev.target.value)}
          defaultValue=""
          className="mt-6 w-full max-w-sm rounded-xl border border-ink-300 bg-white px-3 py-2.5 text-sm focus:border-inkblack focus:outline-none focus:ring-2 focus:ring-highlighter"
        >
          <option value="" disabled>Choose a client…</option>
          {entities.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
        </select>
      )}

      <p className="mt-6 text-xs text-ink-500">You can switch clients any time from the picker in the header.</p>
    </div>
  );
}

export function Loading({ label }: { label: string }) {
  return (
    <div className="flex items-center justify-center gap-2 px-6 py-14 text-sm text-ink-500">
      <Loader2 className="h-4 w-4 animate-spin" /> {label}
    </div>
  );
}

export function ErrBox({ msg }: { msg: string }) {
  return <div className="rounded-2xl border border-danger-200 bg-danger-50 px-6 py-4 text-sm text-danger-700">Failed to load: {msg}</div>;
}

/**
 * Empty state that names the thing AND says why it matters — these tables are
 * empty by default for every new client, so "no rows" alone reads like a bug.
 */
export function EmptyState({ what, why }: { what: string; why: string }) {
  return (
    <div className="px-6 py-12 text-center">
      <p className="text-sm font-medium text-ink-700">No {what} yet</p>
      <p className="mx-auto mt-1.5 max-w-lg text-sm text-ink-500">{why}</p>
    </div>
  );
}

/**
 * px-6, not px-5: every card body, section heading and inline form in these
 * tabs is px-6, so a px-5 cell put the first column 4px left of the heading
 * directly above it. Visible once a table and a heading share one card.
 */
export function Th({ children, right, stick }: { children: React.ReactNode; right?: boolean; stick?: boolean }) {
  return (
    <th className={`px-6 py-3 font-medium ${right ? 'text-right' : 'text-left'} ${
      stick ? 'sticky right-0 z-10 bg-ink-50' : ''
    }`}>{children}</th>
  );
}

/**
 * `stick` pins a cell to the right edge. Used for the Actions column: these
 * tables carry enough compliance columns to overflow a laptop, and an Edit
 * button you have to scroll sideways to reach is one people do not find.
 */
export function Td({ children, right, mono, muted, stick }: {
  children: React.ReactNode; right?: boolean; mono?: boolean; muted?: boolean; stick?: boolean;
}) {
  return (
    <td className={`px-6 py-2.5 ${right ? 'text-right' : ''} ${mono ? 'font-mono text-xs' : ''} ${
      muted ? 'text-ink-500' : 'text-ink-700'
    } ${stick ? 'sticky right-0 z-10 bg-white' : ''}`}>
      {children}
    </td>
  );
}

export function Pill({ tone, children }: { tone: 'ok' | 'warn' | 'danger' | 'neutral' | 'brand'; children: React.ReactNode }) {
  const map = {
    ok: 'bg-ok-50 text-ok-700',
    warn: 'bg-warn-50 text-warn-700',
    danger: 'bg-danger-50 text-danger-700',
    neutral: 'bg-ink-100 text-ink-600',
    brand: 'bg-highlighter-40 text-inkblack',
  } as const;
  return <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${map[tone]}`}>{children}</span>;
}
